import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { windowLabel, type ScopeWindow } from '../../dashboard/utils/scopeWindow';
import { formatNumber } from '../../dashboard/utils/formatNumber';
import { USAGE_META, type EngineerMomentum, type FlowTotals, type Granularity } from './adminMetrics';

/* ============================================================
 * Admin report export — WYSIWYG: exactly the rows the current
 * filters + window + granularity show.
 * ============================================================ */

export type AdminExportFormat = 'csv' | 'xlsx' | 'pdf';

export interface AdminBreakdownRow {
  name: string;
  created: number;
  scheduled: number;
  completed: number;
  skipped: number;
  pending: number;
  completionPct: number | null;
  skipPct: number | null;
  activeEngineers: number;
}

export interface AdminReportPayload {
  scopePath: string[];
  window: ScopeWindow;
  granularity: Granularity;
  kpis: Array<{ label: string; value: string; delta: string }>;
  totals: FlowTotals;
  breakdownLevel: string;
  breakdown: AdminBreakdownRow[];
  momentum: EngineerMomentum[];
  insights: string[];
}

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'all';

export function adminReportFilename(payload: AdminReportPayload, ext: AdminExportFormat): string {
  const scopeSlug =
    payload.scopePath.length > 1 ? payload.scopePath.slice(1).map(slug).join('-') : 'all';
  return `sorigin-admin_${scopeSlug}_${payload.window.from}_${payload.window.to}.${ext}`;
}

const pct = (v: number | null) => (v === null ? '—' : `${v}%`);

function breakdownRows(payload: AdminReportPayload): Array<Record<string, string | number>> {
  const generatedAt = dayjs().format('YYYY-MM-DD HH:mm');
  return payload.breakdown.map((row, i) => ({
    Rank: i + 1,
    [payload.breakdownLevel]: row.name,
    Created: row.created,
    Scheduled: row.scheduled,
    Completed: row.completed,
    Skipped: row.skipped,
    Pending: row.pending,
    'Completion % (of scheduled)': pct(row.completionPct),
    'Skip % (of scheduled)': pct(row.skipPct),
    'Active engineers': row.activeEngineers,
    Scope: payload.scopePath.join(' → '),
    Granularity: payload.granularity,
    From: payload.window.from,
    To: payload.window.to,
    'Generated at': generatedAt,
  }));
}

function momentumRows(payload: AdminReportPayload): Array<Record<string, string | number>> {
  return payload.momentum.map((row, i) => ({
    Rank: i + 1,
    Engineer: row.name,
    Usage: USAGE_META[row.usage].label,
    'Change %': row.changePct === null ? '—' : `${row.changePct > 0 ? '+' : ''}${row.changePct}%`,
    'App actions': row.actions,
    Completed: row.completed,
    Skipped: row.skipped,
    'Last active': row.lastActiveAt ?? 'Never',
    From: payload.window.from,
    To: payload.window.to,
  }));
}

function exportCsv(payload: AdminReportPayload): void {
  const rows = breakdownRows(payload);
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0] ?? {});
  const escape = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h] ?? '')).join(','))].join('\n');
  saveAs(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }), adminReportFilename(payload, 'csv'));
}

function exportXlsx(payload: AdminReportPayload): void {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(payload.kpis.map((k) => ({ Metric: k.label, Value: k.value, Delta: k.delta }))),
    'KPIs'
  );
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(breakdownRows(payload)), 'Task flow');
  if (payload.momentum.length > 0) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(momentumRows(payload)), 'Engineer usage');
  }
  XLSX.writeFile(wb, adminReportFilename(payload, 'xlsx'));
}

/* Sovereign print tokens + the Admin petrol accent. */
const PDF = {
  paper: [242, 234, 219] as const,
  plate: [250, 247, 238] as const,
  petrol: [20, 96, 107] as const,
  racing: [11, 59, 46] as const,
  ink: [42, 31, 23] as const,
  oxblood: [159, 18, 57] as const,
};

function exportPdf(payload: AdminReportPayload): void {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;

  doc.setFillColor(...PDF.paper);
  doc.rect(0, 0, pageWidth, 92, 'F');
  doc.setTextColor(...PDF.racing);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(19);
  doc.text('SORIGIN', margin, 38);
  doc.setFontSize(10);
  doc.setTextColor(...PDF.petrol);
  doc.text('PLATFORM ANALYTICS · ADMIN REPORT', margin, 54);
  doc.setDrawColor(...PDF.petrol);
  doc.setLineWidth(1.2);
  doc.line(margin, 64, pageWidth - margin, 64);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...PDF.ink);
  doc.text(
    `${payload.scopePath.join('  →  ')}   ·   ${windowLabel(payload.window)}   ·   ${payload.granularity} granularity   ·   generated ${dayjs().format('D MMM YYYY, HH:mm')}`,
    margin,
    80
  );

  // KPI strip.
  autoTable(doc, {
    startY: 104,
    margin: { left: margin, right: margin },
    head: [payload.kpis.map((k) => k.label)],
    body: [payload.kpis.map((k) => `${k.value}  (${k.delta})`)],
    styles: { font: 'helvetica', fontSize: 8.5, textColor: [...PDF.ink] as [number, number, number], fillColor: [...PDF.plate] as [number, number, number], cellPadding: 5 },
    headStyles: { fillColor: [...PDF.petrol] as [number, number, number], textColor: [250, 247, 238] as [number, number, number], fontSize: 7.5, fontStyle: 'bold' },
  });

  // Task-flow breakdown.
  const afterKpis = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...PDF.racing);
  doc.text(
    `Task flow by ${payload.breakdownLevel.toLowerCase()} — ${formatNumber(payload.totals.scheduled)} scheduled · ${pct(payload.totals.completionPct)} completed · ${pct(payload.totals.skipPct)} skipped · net backlog ${payload.totals.netFlow >= 0 ? '+' : ''}${formatNumber(payload.totals.netFlow)}`,
    margin,
    afterKpis + 24
  );
  autoTable(doc, {
    startY: afterKpis + 32,
    margin: { left: margin, right: margin },
    head: [['#', payload.breakdownLevel, 'Created', 'Completed', 'Skipped', 'Pending', 'Completion', 'Skip', 'Engineers']],
    body: payload.breakdown.map((row, i) => [
      String(i + 1),
      row.name,
      formatNumber(row.created),
      formatNumber(row.completed),
      formatNumber(row.skipped),
      formatNumber(row.pending),
      pct(row.completionPct),
      pct(row.skipPct),
      String(row.activeEngineers),
    ]),
    styles: { font: 'helvetica', fontSize: 8.5, textColor: [...PDF.ink] as [number, number, number], fillColor: [...PDF.plate] as [number, number, number], lineColor: [...PDF.petrol] as [number, number, number], lineWidth: 0.3, cellPadding: 4 },
    headStyles: { fillColor: [...PDF.racing] as [number, number, number], textColor: [250, 247, 238] as [number, number, number], fontSize: 8, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [246, 241, 231] as [number, number, number] },
  });

  // Top movers + anomalies.
  let y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 24;
  const ensureRoom = (needed: number) => {
    if (y > doc.internal.pageSize.getHeight() - needed) {
      doc.addPage();
      y = 50;
    }
  };
  const movers = payload.momentum.filter((m) => m.usage === 'growing').slice(0, 3);
  const decliners = payload.momentum.filter((m) => m.usage === 'declining' || m.usage === 'dormant').slice(0, 3);
  const writeList = (title: string, color: readonly [number, number, number], lines: string[]) => {
    if (lines.length === 0) return;
    ensureRoom(40 + lines.length * 13);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...color);
    doc.text(title, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...PDF.ink);
    lines.forEach((line, i) => doc.text(`${i + 1}. ${line}`, margin + 8, y + 15 + i * 13));
    y += 15 + lines.length * 13 + 16;
  };
  writeList(
    'Fastest growing engineers',
    PDF.racing,
    movers.map((m) => `${m.name} — ${m.changePct !== null && m.changePct > 0 ? '+' : ''}${m.changePct ?? '—'}% · ${formatNumber(m.actions)} actions`)
  );
  writeList(
    'Declining / dormant engineers',
    PDF.oxblood,
    decliners.map((m) => `${m.name} — ${USAGE_META[m.usage].label}${m.changePct !== null ? ` (${m.changePct > 0 ? '+' : ''}${m.changePct}%)` : ''}`)
  );
  writeList('Anomalies', PDF.petrol, payload.insights.slice(0, 5));

  doc.save(adminReportFilename(payload, 'pdf'));
}

export function exportAdminReport(payload: AdminReportPayload, format: AdminExportFormat): void {
  if (format === 'csv') exportCsv(payload);
  else if (format === 'xlsx') exportXlsx(payload);
  else exportPdf(payload);
}
